import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import "./GroupedRechargeClaimPopup.scss";
import { Icon } from "../../../../Icon";
import { OptimizedImage } from "../../../../Base";
import { useCurrencySymbol } from "../../../../../hooks/useUtils";
import { useI18n } from "@/i18n/hooks";
import utils from "@/utils/utils";
import MoneyImg from "@/assets/event/groupedRechargeMoney.webp";
import WalletStud from "@/assets/event/groupedRechargeWalletStud.svg";
import { popupDismissKeyFromItem } from "../../../groupedRechargeClaimReadStorage";

export interface GroupedRechargeClaimItem {
  /** 业务侧 claim；有 ledgerId 时本地去重以 ledger 为准 */
  claimId: string;
  /** 流水主键：关弹 / read 优先传 */
  ledgerId?: string;
  /** 第几轮发放（与后台对齐；本弹窗不单独展示） */
  grantRoundNo?: number;
  taskGroupId?: string;
  activityName?: string;
  iconUrl?: string;
  bonusIconUrl?: string;
  rewardAmount: number;
}

interface GroupedRechargeClaimPopupProps {
  /** 待展示的 popupItems 列表（按顺序逐条展示） */
  items: GroupedRechargeClaimItem[];
  /** 每条关闭：已读 + 调 read 接口 */
  onDismiss: (item: GroupedRechargeClaimItem) => void;
  /** 所有弹窗关闭后调用 */
  onAllClosed: () => void;
  /** 弹窗会话开始（冻结队列后），用于队列层在接口清空 popupItems 时仍保持本轮展示 */
  onCarouselSessionStart?: () => void;
}

export const GroupedRechargeClaimPopup: React.FC<
  GroupedRechargeClaimPopupProps
> = ({ items, onDismiss, onAllClosed, onCarouselSessionStart }) => {
  /** 展示会话内冻结队列：避免父级 popupItems 被接口清空后队列 prematurely complete，或 key/remount 打乱顺序 */
  const frozenQueueRef = useRef<GroupedRechargeClaimItem[] | null>(null);
  const [, rerender] = useState(0);
  const sessionStartedRef = useRef(false);
  const [entered, setEntered] = useState(false);
  /** 弹窗切换间隔期：true 时隐藏弹窗，给用户一段空白感知间隔 */
  const [isTransitioning, setIsTransitioning] = useState(false);
  const rafRef = useRef<number | null>(null);
  const transitionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const triggerEnterAnimation = useCallback(() => {
    setEntered(false);
    if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = requestAnimationFrame(() => {
        setEntered(true);
        rafRef.current = null;
      });
    });
  }, []);

  // 组件卸载时清理计时器
  useEffect(() => {
    return () => {
      if (transitionTimerRef.current !== null) {
        clearTimeout(transitionTimerRef.current);
      }
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, []);

  if (frozenQueueRef.current === null && items.length > 0) {
    frozenQueueRef.current = [...items];
  }

  // 入账条可能先入队冻结，稍后配置才补全 popupItems：把晚到的条目按 ledger/claim 主键并入队尾（去重）。
  useEffect(() => {
    const q = frozenQueueRef.current;
    if (!q?.length || !items.length) return;

    const seen = new Set(
      q
        .map(
          it =>
            popupDismissKeyFromItem(it) ||
            (it.claimId ? String(it.claimId).trim() : "")
        )
        .filter(Boolean)
    );
    let added = false;
    for (const it of items) {
      const key =
        popupDismissKeyFromItem(it) ||
        (it.claimId ? String(it.claimId).trim() : "");
      if (!key || seen.has(key)) continue;
      seen.add(key);
      q.push({ ...it });
      added = true;
    }
    if (added) rerender(n => n + 1);
  }, [items]);

  useLayoutEffect(() => {
    if (
      frozenQueueRef.current &&
      frozenQueueRef.current.length > 0 &&
      !sessionStartedRef.current
    ) {
      sessionStartedRef.current = true;
      onCarouselSessionStart?.();
    }
  }, [items.length, onCarouselSessionStart]);

  // 队列首次出现 / 每次切换下一条时播放进场动画
  const currentClaimId = frozenQueueRef.current?.[0]?.claimId ?? null;
  useEffect(() => {
    if (currentClaimId === null) return;
    triggerEnterAnimation();
    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [currentClaimId]);

  const queue = frozenQueueRef.current;

  const currSymbol = useCurrencySymbol();
  const { t, currentLanguage } = useI18n();
  const subtitleEsSingleLine =
    typeof currentLanguage === "string" && currentLanguage.startsWith("es");

  if (!queue || queue.length === 0) return null;
  if (isTransitioning) return null;

  const current = queue[0];
  const iconUrl = current.bonusIconUrl ?? current.iconUrl;

  const handleClose = () => {
    if (isTransitioning) return;
    onDismiss(current);
    frozenQueueRef.current?.shift();
    if (!frozenQueueRef.current?.length) {
      frozenQueueRef.current = null;
      sessionStartedRef.current = false;
      setEntered(false);
      onAllClosed();
    } else {
      // 隐藏当前弹窗，短暂空白后再显示下一条（进场动画）
      setEntered(false);
      setIsTransitioning(true);
      transitionTimerRef.current = setTimeout(() => {
        setIsTransitioning(false);
        rerender(n => n + 1);
        transitionTimerRef.current = null;
      }, 420); // 260ms CSS transition + buffer
    }
  };

  // 挂到 body，避免 #root/.phone-wrapper 的 flex / overflow 影响全屏 overlay
  const container = document.body;

  return createPortal(
    <div
      className={["grc-popup", entered ? "grc-popup--entered" : ""]
        .filter(Boolean)
        .join(" ")}
    >
      {/* 暗色遮罩 */}
      <div className="grc-popup__mask" onClick={handleClose} />

      <div className="grc-popup__container">
        {/* 钱堆图片 */}
        <div className="grc-popup__hero">
          <img src={MoneyImg} alt="money" className="grc-popup__hero-img" />
        </div>

        {/* 中间背景块 */}
        <div className="grc-popup__cards-bg" />

        {/* 橙色奖励卡（搭扣挂在卡左侧） */}
        <div className="grc-popup__card">
          <img
            src={WalletStud}
            alt=""
            className="grc-popup__wallet-stud"
            aria-hidden
          />

          {/* 奖励图标 */}
          {iconUrl && (
            <div className="grc-popup__reward-icon">
              <OptimizedImage
                src={utils.getImageUrl(iconUrl) ?? iconUrl}
                alt="reward"
                width="5rem"
                height="5rem"
              />
            </div>
          )}

          {/* 奖励金额 */}
          <div className="grc-popup__total">
            <span className="grc-popup__total-prefix">
              {t("event.groupedRecharge.readyPrefix", {
                defaultValue: "Your",
              })}{" "}
            </span>
            <span className="grc-popup__total-amount">
              {currSymbol}
              {current.rewardAmount}
            </span>
            <span className="grc-popup__total-suffix">
              {" "}
              {t("event.groupedRecharge.readySuffix", {
                defaultValue: "Bonus is Ready!",
              })}
            </span>
          </div>

          {/* 副标题 */}
          <div
            className={[
              "grc-popup__subtitle",
              subtitleEsSingleLine ? "grc-popup__subtitle--es-single-line" : "",
            ]
              .filter(Boolean)
              .join(" ")}
          >
            {t("event.groupedRecharge.readyHint", {
              defaultValue: "Enjoy the extra boost and good luck!",
            })}
          </div>
        </div>

        {/* OK 按钮（卡片外部独立，与 GroupedRechargeOffer 保持一致） */}
        <div className="grc-popup__btn-wrap">
          <button className="grc-popup__btn" onClick={handleClose}>
            {t("common.ok", { defaultValue: "OK" })}
          </button>
        </div>

        {/* 关闭按钮 */}
        <div
          className="grc-popup__close"
          onClick={handleClose}
          role="button"
          aria-label="close"
        >
          <Icon name="closeDialog" color="#ffffff" />
        </div>
      </div>
    </div>,
    container
  );
};

export default GroupedRechargeClaimPopup;
